import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowLeftRight } from "lucide-react";
import dayjs from "dayjs";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { message, Select } from "antd";
import Spinner from "@/components/Spinner";
import { SearchReferral, UserPackageRefferalAPI } from "@/api/Admin/type";
import { fetchUserData, searchReferral } from "@/api/Admin/User";
import { changeReferralUserOfPackage } from "@/api/Admin/adminPackage";
import { getUserPackagesRefferal } from "@/api/Admin/userPackage";

const fmt = (n: number) =>
  n.toLocaleString("vi-VN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const getInitials = (name: string) => {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return initials || "N/A";
};

const getPackageOptionLabel = (pkg: UserPackageRefferalAPI["data"][number]) => {
  const referrerName = pkg.referrer_info.user_info.fullname
    || pkg.referrer_info.referrer_name
    || pkg.referrer_info.referrer_email;

  return `${referrerName} - ${pkg.package_name}`;
};

const TransferInvestorPage: React.FC = () => {
  const { investorId } = useParams<{ investorId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loadingTransferData, setLoadingTransferData] = useState(false);
  const [submittingTransferReferral, setSubmittingTransferReferral] = useState(false);
  const [searchingReferral, setSearchingReferral] = useState(false);
  const [referralOptions, setReferralOptions] = useState<SearchReferral[]>([]);
  const [selectedReferralCode, setSelectedReferralCode] = useState<string>();
  const [investorName, setInvestorName] = useState("");
  const [packageReferrals, setPackageReferrals] = useState<UserPackageRefferalAPI["data"]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<number>();
  const referralSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedStaff = referralOptions.find((staff) => staff.referral_code === selectedReferralCode) ?? null;
  const packageReferral = packageReferrals.find((pkg) => pkg.id === selectedPackageId) ?? null;
  const currentStaff = packageReferral?.referrer_info.user_info.fullname
    || packageReferral?.referrer_info.referrer_name
    || packageReferral?.referrer_info.referrer_email
    || "N/A";

  const commissionSummary = packageReferral?.commission_summary;
  const paidAmount = commissionSummary?.total_paid ?? 0;
  const remainingAmount = commissionSummary?.total_unpaid ?? 0;
  const remainingMonths = commissionSummary?.unpaid_months ?? 0;

  const loadTransferData = async () => {
    if (!investorId) {
      setPackageReferrals([]);
      setSelectedPackageId(undefined);
      setInvestorName("");
      return;
    }

    const parsedInvestorId = Number(investorId);
    if (Number.isNaN(parsedInvestorId)) {
      message.error(t("transfer.invalidInvestor", "Nhà đầu tư không hợp lệ"));
      setPackageReferrals([]);
      setSelectedPackageId(undefined);
      setInvestorName("");
      return;
    }

    setLoadingTransferData(true);
    try {
      const [investor, referralResponse] = await Promise.all([
        fetchUserData(investorId),
        getUserPackagesRefferal(parsedInvestorId),
      ]);

      setInvestorName(investor.fullname || investor.email || `User #${investorId}`);
      setPackageReferrals(referralResponse.data || []);
      setSelectedPackageId((currentValue) => {
        if ((referralResponse.data || []).some((pkg) => pkg.id === currentValue)) {
          return currentValue;
        }

        return referralResponse.data?.[0]?.id;
      });
    } catch (error) {
      setPackageReferrals([]);
      setSelectedPackageId(undefined);
      setInvestorName("");
      console.error("Failed to load transfer data:", error);
    } finally {
      setLoadingTransferData(false);
    }
  };

  useEffect(() => {
    void loadTransferData();
  }, [investorId]);

  useEffect(() => {
    return () => {
      if (referralSearchTimeoutRef.current) {
        clearTimeout(referralSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchReferral = async (keyword: string) => {
    const normalizedKeyword = keyword.trim();
    if (referralSearchTimeoutRef.current) {
      clearTimeout(referralSearchTimeoutRef.current);
    }

    if (normalizedKeyword.length < 2) {
      setReferralOptions([]);
      setSearchingReferral(false);
      return;
    }

    setSearchingReferral(true);
    referralSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchReferral(normalizedKeyword);
        const responseData = response?.data;
        const items = Array.isArray(responseData)
          ? responseData
          : responseData
            ? [responseData]
            : [];
        setReferralOptions(items);
      } catch (error) {
        setReferralOptions([]);
        console.error('Failed to load referral options:', error);
      } finally {
        setSearchingReferral(false);
      }
    }, 2000);
  };

  const handleTransferReferral = async (values: any) => {
    if (!packageReferral || !values?.newReferralCode || !values?.transferDate) {
      return;
    }

    setSubmittingTransferReferral(true);
    try {
      await changeReferralUserOfPackage(
        packageReferral.id,
        values.newReferralCode,
        values.transferDate.format('YYYY-MM-DD')
      );
      message.success(t('investment.transferReferralSuccess', 'Chuyển người giới thiệu thành công'));
      setSelectedReferralCode(undefined);
      setReferralOptions([]);
      await loadTransferData();
    } catch (error) {
      console.error('Failed to transfer referral:', error);
    } finally {
      setSubmittingTransferReferral(false);
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("investors.title"), path: "/admin/investors" },
    { label: t("transfer.title", "Chuyển giao khách hàng") },
  ];

  if (loadingTransferData) {
    return <Spinner />;
  }

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("transfer.title", "Chuyển giao khách hàng")} - Admin</title>
      </Helmet>

      <PageHeader
        title={t("transfer.title", "Chuyển giao khách hàng")}
        description={t("transfer.subtitle", "Điều chuyển nhà đầu tư sang nhân viên khác và phân bổ doanh số hoa hồng")}
        breadcrumbs={breadcrumbs}
      />

      <div className="max-w-5xl space-y-5">
        {/* Customer card */}
        <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold text-lg">
              {getInitials(investorName || `User ${investorId ?? ""}`)}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">
                {t("transfer.customerLabel", "Khách hàng")} (ID: {investorId ?? "-"})
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {investorName || t("transfer.noInvestorName", "Không có thông tin nhà đầu tư")}
              </p>
              {packageReferral && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t("transfer.currentPackage", "Gói hiện tại")}: {packageReferral.package_name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Staff transfer panel */}
        <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-end gap-3">
            {/* Current staff */}
            <div className="flex-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                {t("transfer.currentStaff", "Nhân viên hiện tại (A)")}
              </label>
              <Select
                value={selectedPackageId}
                onChange={(value) => setSelectedPackageId(value)}
                placeholder={t("transfer.selectCurrentStaff", "Chọn nhân viên hiện tại")}
                options={packageReferrals.map((pkg) => ({
                  value: pkg.id,
                  label: getPackageOptionLabel(pkg),
                }))}
                className="px-3 py-2.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 min-h-[42px] flex items-center w-full"
              />
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0 mb-1">
              <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* New staff select */}
            <div className="flex-1">
              <Select
                showSearch
                filterOption={false}
                value={selectedReferralCode}
                onChange={(value) => setSelectedReferralCode(value)}
                onSearch={handleSearchReferral}
                placeholder={t('investment.newReferralCodePlaceholder', 'Nhập mã giới thiệu')}
                loading={searchingReferral}
                notFoundContent={searchingReferral ? t('common.loading', 'Đang tải...') : t('investment.noReferralFound', 'Không tìm thấy mã giới thiệu')}
                options={referralOptions.map((referral) => ({
                  value: referral.referral_code,
                  label: `${referral.referral_code} - ${referral.referrer_name || referral.referrer_email}${referral.referrer_phone ? ` - ${referral.referrer_phone}` : ''}`,
                }))}
                className="px-3 py-2.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 min-h-[42px] flex items-center w-full"
              />
            </div>
          </div>
        </div>

        {/* Commission preview */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-md border border-indigo-100 dark:border-indigo-800 p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-4">
            {t("transfer.commissionPreview", "Dự tính hoa hồng chuyển giao")}
          </h3>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 font-medium mb-1.5">
                {t("transfer.paid", "Đã thanh toán")}
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {fmt(paidAmount)} đ
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {t("transfer.paidNote", "Đã trả cho NV hiện tại (A)")}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-1.5 text-xs text-orange-500 dark:text-orange-400 font-medium mb-1.5">
                {t("transfer.remaining", "Còn lại")} {remainingMonths} {t("transfer.monthsUnpaid", "tháng chưa thanh toán")}
              </div>
              <p className="text-lg font-bold text-orange-500 dark:text-orange-400">
                {fmt(remainingAmount)} đ
              </p>
              <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">
                {t("transfer.remainingNote", "Dành cho nhân viên mới (B)")}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-indigo-100 dark:border-indigo-800">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("transfer.totalForB", "Tổng hoa hồng NV B sẽ nhận")} ({remainingMonths} {t("transfer.months", "tháng")}):
            </span>
            <span className="text-base font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded-md">
              + {fmt(remainingAmount)} đ
            </span>
          </div>
        </div>

        {!packageReferral && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800 p-4 text-sm text-yellow-700 dark:text-yellow-300">
            {t("transfer.noActivePackage", "Không tìm thấy gói đầu tư đang hoạt động cho nhà đầu tư này")}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pb-6">
          <button
            onClick={() => navigate("/admin/investors")}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            {t("common.cancel", "Hủy bỏ")}
          </button>
          <button
            onClick={() => void handleTransferReferral({
              newReferralCode: selectedReferralCode,
              transferDate: dayjs(),
            })}
            disabled={!selectedStaff || !packageReferral || submittingTransferReferral}
            className="px-6 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {t("transfer.confirm", "Xác nhận chuyển giao")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferInvestorPage;
